import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { commonTableStyles as styles } from "../styles";
import { ReducersModal } from "utils/modals";
import { UPDATE_SERVICE_PRICING_REDUCER } from "store/actionTypes";
import {
  getCategoryTypeOptions,
  getServicePricingListing,
} from "modules/servicePricingManagement/action";
import ContainedButton from "components/button/contained-button";
import UpdatePriceModal from "modules/servicePricingManagement/updatePriceModal";
import { getTableTheme } from "../tableTheme";
import "../style.css";
import { formatCurrency } from "utils/commonFunctions";
import MUIDataTable from "mui-datatables";
import CategoryTabs from "components/category/CategoryTabs";
import {
  SERVICE_CATEGORY,
  SERVICE_CATEGORY_LABEL,
  hasCategoryTypeFilter,
  toServiceCategory,
} from "utils/serviceCategory";
import ROUTE_NAMES from "routes/routeNames";
import moment from "moment";

const ServicePricingTable = () => {
  const [open, setOpen] = useState(false);
  const {
    result,
    pagination,
    category,
    categoryTypeId,
    categoryTypes,
    page,
    limit,
  } = useSelector((state: ReducersModal) => state.ServicePricingReducer);
  const dispatch: any = useDispatch();

  const dispatchValue = (payload: any) => {
    dispatch({
      type: UPDATE_SERVICE_PRICING_REDUCER,
      payload,
    });
  };

  const showTypeFilter = hasCategoryTypeFilter(category);
  const typeLabel = `${SERVICE_CATEGORY_LABEL[toServiceCategory(category)]} Type`;

  useEffect(() => {
    dispatch(getCategoryTypeOptions());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  useEffect(() => {
    dispatch(getServicePricingListing());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, categoryTypeId, page, limit]);

  const handleCategoryChange = (nextCategory: SERVICE_CATEGORY) => {
    if (nextCategory === toServiceCategory(category)) return;
    dispatchValue({
      category: nextCategory,
      categoryTypeId: "",
      categoryTypes: [],
      page: 1,
      result: [],
    });
  };

  const handleCategoryTypeChange = (nextId: string) => {
        dispatchValue({ categoryTypeId: nextId, page: 1, result: [] });
  };

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    dispatchValue({ page: newPage + 1 });
  };

  const handleChangeRowsPerPage = (numberOfRows: number) => {
    dispatchValue({ limit: numberOfRows, page: 1 });
  };
    const columns: any = [
    {
      name: "serviceType",
      label: "Service Type",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any, rowData: any) => {
          const label =
            typeof value === "string" ? value : value?.serviceType || "";
          return <div>{label || "N/A"}</div>;
        },
      },
    },
    {
      name: "serviceDuration",
      label: "Service Duration",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any) => {
          return <div>{value || "N/A"}</div>;
        },
      },
    },
    {
      name: "price",
      label: "Price",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any) => {
          if (value || value === 0) return <div>{formatCurrency(value)}</div>;
          return <div>{`N/A`}</div>;
        },
      },
    },
    {
      name: "createdOn",
      label: "Created On",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any) => {
          return (
            <div>
              {value ? moment(value).format("DD/MM/YYYY") : "N/A"}
            </div>
          );
        },
      },
    },
  ];

  const options: any = {
    print: false,
    filter: false,
    download: false,
    serverSide: true,
    viewColumns: false,
    selectableRows: "none",
    search: false,
    count: pagination?.totalRecords,
    page: Math.max((pagination?.page || 1) - 1, 0),
    rowsPerPage: pagination.limit,
    onChangePage: (currentPage: number) => handleChangePage(null, currentPage),
    onChangeRowsPerPage: (numberOfRows: number) =>
      handleChangeRowsPerPage(numberOfRows),
    rowsPerPageOptions: [10, 20, 50, 100],
  };
  const TableComponent: any = MUIDataTable;

  return (
    <>
      <Box sx={{ ...styles.searchAndFilter, marginBottom: "16px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <CategoryTabs
            basePath={ROUTE_NAMES.MAIN_ROUTES.SERVICE_PRICING_MANAGEMENT}
            onChange={handleCategoryChange}
          />
          {showTypeFilter && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel
                shrink={Boolean(categoryTypeId)}
                sx={!categoryTypeId ? { display: "none" } : undefined}
              >
                {typeLabel}
              </InputLabel>
              <Select
                label={typeLabel}
                displayEmpty
                notched={Boolean(categoryTypeId)}
                value={categoryTypeId || ""}
                renderValue={(selected) => {
                  const selectedId = String(selected || "");
                  if (!selectedId) return typeLabel;
                  const match = (categoryTypes || []).find(
                    (item: any) =>
                      String(item._id || item.id) === selectedId
                  );
                  return match?.label || typeLabel;
                }}
                onChange={(e) =>
                  handleCategoryTypeChange(String(e.target.value || ""))
                }
              >
                <MenuItem value="">All</MenuItem>
                {(categoryTypes || []).map((item: any) => (
                  <MenuItem key={item._id || item.id} value={item._id || item.id}>
                    {item.label || "N/A"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        <Box sx={styles.searchAndFilter}>
          <ContainedButton
            externalStyles={styles.HeadBtns}
            onClick={() => {
              setOpen(true);
            }}
            title={"Update Pricing"}
          />
        </Box>
        <UpdatePriceModal state={open} setState={setOpen} />
      </Box>
      <Box sx={styles.paper}>
        <ThemeProvider theme={getTableTheme()}>
          <TableComponent
            title={
              <Typography variant="h5" sx={styles.tableHeadingTxt}>
                {`Service Pricing Table`}
              </Typography>
            }
            data={result}
            columns={columns}
            options={options}
          />
        </ThemeProvider>
      </Box>
    </>
  );
};

export default ServicePricingTable;
